import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import {
    AjaxListingContext,
    InfinitePagination
} from '$partials/base/ajax-listing.jsx';

import {
    Header,
    Footer,
    Meta,
    StickyCTAButtons
} from '$partials/global';

import {
    AuthorityItem,
    PersonItem
} from '$partials/listings';

import { CommunityFilterDialog } from '$partials/filters/index.js';
import { useAjaxListing } from '$hooks/ajax-listing';
import { useSingleSelectFilter } from '$hooks/filters';
import Button from '$partials/ui/button.jsx';
import IconChevronDown from '$partials/icons/icon-chevron-down';
import Select from '$partials/filters/select.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const CommunityListingPage = (props) => {
    // references
    const watchedElemRef = useRef();

    const page = props.data.page;

    // UI states
    const [ headerHeight, setHeaderHeight ] = useState();
    const [ isFiltersOpen, setIsFiltersOpen ] = useState(false);

    const disciplineFilter = useSingleSelectFilter(page?.items?.filters?.discipline);
    const orderByFilter = useSingleSelectFilter(page?.items?.filters?.orderBy);
    const personAlumniYearFilter = useSingleSelectFilter(page?.items?.filters?.personAlumniYear);
    const personTypeFilter = useSingleSelectFilter(page?.items?.filters?.personType);
    const regionFilter = useSingleSelectFilter(page?.items?.filters?.region);
    const typeFilter = useSingleSelectFilter(page?.items?.filters?.type);

    const listing = useAjaxListing({
        infinite: true,
        items: page?.items?.edges,
        page: page?.items?.pageInfo?.page || 1,
        path: `/api/v1/listings/${encodeURIComponent(props.data.uri)}`,
        totalItems: page?.items?.total,
        totalPages: page?.items?.totalPages,
        query: {
            'discipline': disciplineFilter.value,
            'orderBy': orderByFilter.value,
            'cohort': personAlumniYearFilter.value,
            'personType': personTypeFilter.value,
            'region': regionFilter.value,
            'type': typeFilter.value,
        }
    });

    const typeFilterOptions = typeFilter.options.map((item) => {
        if (item === 'authoritydetailpage') {
            item = {};
            item.label = 'Authority Partners';
            item.value = 'authoritydetailpage';
            return item;
        } else {
            item = {};
            item.label = 'People';
            item.value = 'persondetailpage';
            return item;
        }
    });

    useEffect(() => {
        if (personTypeFilter.value !== 'alum') {
            personAlumniYearFilter.setValue('');
        }
    }, [ personTypeFilter.value ]);

    const clearAllFilters = () => {
        disciplineFilter.setValue('');
        orderByFilter.setValue('');
        personAlumniYearFilter.setValue('');
        personTypeFilter.setValue('');
        regionFilter.setValue('');
    };

    const hasFilters = [
        disciplineFilter.value,
        orderByFilter.value,
        personAlumniYearFilter.value,
        personTypeFilter.value,
        regionFilter.value
    ].filter(Boolean).length > 0;

    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText}
                domain={props.domain}
                image={page.listingImage}
                page={page} />

            <Header
                {...props?.data?.navigation}
                {...props?.data?.ticker}
                headerHeight={(height) => setHeaderHeight(height)}
                pageType={page?.__typename}
                standfirst={page?.standfirst}
                watchedElemRef={watchedElemRef} />

            <StickyCTAButtons
                cta1={page?.stickyCta1?.[0]}
                cta2={page?.stickyCta2?.[0]} />

            <main
                id='main-content'
                style={{marginTop: `${headerHeight}px`}}>

                <section
                    className='layout-container pt-30 sm:pt-20 md:pt-0 mb-60 md:mb-100 title-height'
                    style={{top: `${headerHeight}px`}}>
                    <div className='layout-grid-negative-gutter md:flex'>
                        <div className='md:w-1/12 layout-grid-col-padding' />

                        <div className='sm:w-8/12 md:w-7/12 layout-grid-col-padding'>
                            {page?.title && (
                                <h1 className='type-style-4 pb-30'>{page.title}</h1>
                            )}

                            {page?.heading && (
                                <p className='type-style-1'>{page.heading}</p>
                            )}
                        </div>
                    </div>
                </section>

                {/* Filter tabs */}
                <section
                    className='relative bg-white layout-container py-30 md:py-40 border-t border-t-charcoal'
                    ref={watchedElemRef}>
                    <div className='layout-grid-negative-gutter md:flex'>
                        <div className='md:w-1/12 layout-grid-col-padding' />

                        <div className='md:w-11/12 layout-grid-col-padding'>
                            <div className='flex gap-10'>
                                {/* Mobile & tablet */}
                                <div className='md:hidden'>
                                    <Select
                                        defaultLabel=''
                                        label='Type filter'
                                        name='type-filter'
                                        onChange={(value) => {
                                            clearAllFilters();
                                            typeFilter.setValue(value);
                                        }}
                                        options={typeFilterOptions}
                                        value={typeFilter.value} />
                                </div>

                                {/* xs/sm Open Filters */}
                                <div className='md:hidden'>
                                    <button
                                        className='rounded border border-charcoal items-center type-style-5 bg-white h-full py-10 px-20 flex'
                                        onClick={setIsFiltersOpen.bind(null, true)}
                                        type='button'>
                                        Filters
                                        <span
                                            aria-hidden='true'
                                            className='pl-10'>
                                            <IconChevronDown />
                                        </span>
                                    </button>

                                    {isFiltersOpen && (
                                        <CommunityFilterDialog
                                            clearAllFilters={clearAllFilters}
                                            disciplineFilter={disciplineFilter}
                                            hasFilters={hasFilters}
                                            isOpen={isFiltersOpen}
                                            onClose={setIsFiltersOpen.bind(null, false)}
                                            orderByFilter={orderByFilter}
                                            personAlumniYearFilter={personAlumniYearFilter}
                                            personTypeFilter={personTypeFilter}
                                            regionFilter={regionFilter}
                                            typeFilter={typeFilter} />
                                    )}
                                </div>
                            </div>

                            <div className='flex justify-between'>
                                {/* Desktop */}
                                <div className='hidden md:flex gap-10'>
                                    <Button
                                        className={`${typeFilter.value === 'persondetailpage' ? 'pill-button pill-button--selected' : 'pill-button'}`}
                                        onClick={() => {
                                            clearAllFilters();
                                            typeFilter.setValue('persondetailpage');
                                        }}
                                        type='button'>
                                        People
                                    </Button>

                                    <Button
                                        className={`${typeFilter.value === 'authoritydetailpage' ? 'pill-button pill-button--selected' : 'pill-button'}`}
                                        onClick={() => {
                                            clearAllFilters();
                                            typeFilter.setValue('authoritydetailpage');
                                        }}
                                        type='button'>
                                        Authority Partners
                                    </Button>
                                </div>

                                <div className='hidden md:flex gap-10'>
                                    {typeFilter.value === 'persondetailpage' && (
                                        <>
                                            <Select
                                                defaultLabel='Role'
                                                label='Person type filter'
                                                name='person-type-filter'
                                                onChange={(value) => personTypeFilter.setValue(value)}
                                                options={[{
                                                    label: personTypeFilter.label,
                                                    value: ''
                                                },
                                                {
                                                    label: 'All',
                                                    value: ''
                                                },
                                                ...personTypeFilter.options]}
                                                value={personTypeFilter.value} />

                                            {(personTypeFilter.value === 'alum') && (
                                                <Select
                                                    defaultLabel='Cohort'
                                                    label='Cohort filter'
                                                    name='person-alumni-year-filter'
                                                    onChange={(value) => personAlumniYearFilter.setValue(value)}
                                                    options={[{
                                                        label: personAlumniYearFilter.label,
                                                        value: ''
                                                    },
                                                    {
                                                        label: 'All',
                                                        value: ''
                                                    },
                                                    ...personAlumniYearFilter.options]}
                                                    value={personAlumniYearFilter.value} />
                                            )}
                                        </>
                                    )}

                                    <Select
                                        defaultLabel='Location'
                                        label='Location filter'
                                        name='region-filter'
                                        onChange={(value) => regionFilter.setValue(value)}
                                        options={[{
                                            label: regionFilter.label,
                                            value: ''
                                        },
                                        {
                                            label: 'All',
                                            value: ''
                                        },
                                        ...regionFilter.options]}
                                        value={regionFilter.value} />

                                    {typeFilter.value === 'persondetailpage' && (
                                        <Select
                                            defaultLabel='Discipline'
                                            label='Discipline filter'
                                            name='discipline-filter'
                                            onChange={(value) => disciplineFilter.setValue(value)}
                                            options={[{
                                                label: disciplineFilter.label,
                                                value: ''
                                            },
                                            {
                                                label: 'All',
                                                value: ''
                                            },
                                            ...disciplineFilter.options]}
                                            value={disciplineFilter.value} />
                                    )}

                                    <Select
                                        defaultLabel='Sort By'
                                        label='Sort by filter'
                                        name='sort-by-filter'
                                        onChange={(value) => orderByFilter.setValue(value)}
                                        options={[{
                                            label: orderByFilter.label,
                                            value: ''
                                        },
                                        ...orderByFilter.options]}
                                        value={orderByFilter.value} />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className='relative bg-white border-t border-t-charcoal py-20 md:py-70'>
                    <AjaxListingContext.Provider value={listing}>
                        {typeFilter.value === 'persondetailpage' && (
                            <section className='layout-container'>
                                <div className='layout-grid-negative-gutter md:flex'>
                                    <div className='md:w-1/12' />
                                    <div className='md:w-11/12 flex flex-wrap'>
                                        {listing?.items?.length > 0 && listing.items.map(({ node: item }, idx) => {
                                            return (
                                                <PersonItem
                                                    index={idx}
                                                    key={idx + item.id}
                                                    {...item} />
                                            );
                                        })}
                                    </div>
                                </div>
                            </section>
                        )}
                        {typeFilter.value === 'authoritydetailpage' && (
                            <section className='layout-container'>
                                <div className='layout-grid-negative-gutter md:flex'>
                                    <div className='md:w-1/12' />
                                    <div className='md:w-11/12 sm:flex sm:flex-wrap'>
                                        {listing?.items?.length > 0 && listing.items.map(({ node: item }, idx) => {
                                            return (
                                                <div
                                                    className='layout-grid-col-padding sm:basis-6/12 md:basis-5/11'
                                                    key={idx}>
                                                    <AuthorityItem
                                                        index={idx}
                                                        key={idx}
                                                        total={listing.items.length}
                                                        {...item} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* No result message */}
                        {(!listing.items?.length && !listing.loading) && (
                            <section className='layout-container py-40'>
                                <div className='layout-grid-negative-gutter md:flex'>
                                    <div className='md:w-1/12 layout-grid-col-padding' />

                                    <div className='md:w-11/12 layout-grid-col-padding'>
                                        <p className='type-style-3'>
                                            No results
                                        </p>
                                    </div>
                                </div>
                            </section>
                        )}
                        {listing?.page < listing?.totalPages && (
                            <InfinitePagination>
                                <section className='layout-container border-t border-t-charcoal py-40'>
                                    <div className='layout-grid-negative-gutter md:flex'>
                                        <div className='md:w-1/12 layout-grid-col-padding' />

                                        <div className='md:w-11/12 layout-grid-col-padding'>
                                            <p className='type-style-4'>
                                                Loading &hellip;
                                            </p>
                                        </div>
                                    </div>
                                </section>
                            </InfinitePagination>
                        )}
                    </AjaxListingContext.Provider>
                </section>
            </main>

            <Footer
                {...props?.data?.footer}
                {...props?.data?.newsletter} />

        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.object,
        footer: PropTypes.object,
        navigation: PropTypes.object,
        newsletter: PropTypes.object,
        ticker: PropTypes.object,
        uri: PropTypes.string
    }),
    domain: PropTypes.string,
};

CommunityListingPage.propTypes = propTypes;

export default CommunityListingPage;
